import { useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { type SyntheticEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Dialog } from '../../components/ui/dialog';
import { Field } from '../../components/ui/field';
import { Checkbox, Input, Select } from '../../components/ui/input';
import { api, errorMessage } from '../../lib/api';
import { useCan } from '../../lib/session';
import type { Product, ProductMedia, PublishProblem, Variant } from '../../lib/types';

/** Базовый адрес картинок (imgproxy/CDN). Не задан — показываем имя файла. */
const MEDIA_URL = (import.meta.env.VITE_MEDIA_URL as string | undefined) ?? '';

function useRefresh(productId: string) {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['catalog', 'product', productId] }),
      queryClient.invalidateQueries({ queryKey: ['catalog', 'products'] }),
    ]);
}

/** Выполняет действие с индикатором и текстом ошибки. */
function useAction() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const run = async (action: () => Promise<unknown>): Promise<boolean> => {
    setBusy(true);
    setError(undefined);
    try {
      await action();
      return true;
    } catch (err) {
      setError(errorMessage(err));
      return false;
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, run };
}

export function PublishSection({ product }: { product: Product }) {
  const { t } = useTranslation();
  const canPublish = useCan('catalog.publish');
  const refresh = useRefresh(product.id);
  const { busy, error, run } = useAction();
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [replacement, setReplacement] = useState('');

  const problemText = (p: PublishProblem) =>
    t(`products.problem.${p.code as ProblemCode}`, { locale: p.locale ?? '' });

  return (
    <Card>
      <CardHeader title={t('common.status')} />
      <CardContent className="grid gap-3">
        {product.status !== 'active' && product.publishProblems.length > 0 ? (
          <Alert variant="warning" title={t('products.notReady')}>
            <ul className="list-disc pl-5">
              {product.publishProblems.map((p, i) => (
                <li key={i}>{problemText(p)}</li>
              ))}
            </ul>
          </Alert>
        ) : null}
        {error ? <Alert variant="destructive">{error}</Alert> : null}
        {canPublish ? (
          <div className="flex flex-wrap gap-2">
            {product.status !== 'active' ? (
              <Button
                disabled={busy || product.publishProblems.length > 0}
                onClick={() =>
                  void run(async () => {
                    await api(`/catalog/products/${product.id}/publish`, { method: 'POST' });
                    await refresh();
                  })
                }
              >
                {t('products.publish')}
              </Button>
            ) : null}
            {product.status !== 'archived' ? (
              <Button
                variant="outline"
                disabled={busy}
                onClick={() => {
                  setArchiveOpen(true);
                }}
              >
                {t('products.archive')}
              </Button>
            ) : null}
          </div>
        ) : null}
      </CardContent>
      <Dialog
        open={archiveOpen}
        onClose={() => {
          setArchiveOpen(false);
        }}
        title={t('products.archive')}
        description={t('products.archiveConfirm')}
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => {
                setArchiveOpen(false);
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  await api(`/catalog/products/${product.id}/archive`, {
                    method: 'POST',
                    body: replacement.trim() ? { replacementProductId: replacement.trim() } : {},
                  });
                  setArchiveOpen(false);
                  await refresh();
                })
              }
            >
              {t('products.archive')}
            </Button>
          </>
        }
      >
        <Field label={t('products.replacement')} hint={t('common.optional')}>
          {(id) => (
            <Input
              id={id}
              value={replacement}
              onChange={(e) => {
                setReplacement(e.target.value);
              }}
            />
          )}
        </Field>
        {error ? <Alert variant="destructive">{error}</Alert> : null}
      </Dialog>
    </Card>
  );
}

type ProblemCode =
  | 'missing_translation'
  | 'no_active_variant'
  | 'missing_price'
  | 'missing_manufacturer'
  | 'manufacturer_address_missing'
  | 'missing_required_attribute'
  | 'missing_category';

export function VariantsSection({ product }: { product: Product }) {
  const { t } = useTranslation();
  const canWrite = useCan('catalog.write');
  const refresh = useRefresh(product.id);
  const { busy, error, run } = useAction();
  const [draft, setDraft] = useState({ sku: '', gtin: '', mpn: '' });

  const add = (e: SyntheticEvent) => {
    e.preventDefault();
    void run(async () => {
      await api(`/catalog/products/${product.id}/variants`, {
        method: 'POST',
        body: {
          sku: draft.sku.trim(),
          ...(draft.gtin.trim() ? { gtin: draft.gtin.trim() } : {}),
          ...(draft.mpn.trim() ? { mpn: draft.mpn.trim() } : {}),
        },
      });
      setDraft({ sku: '', gtin: '', mpn: '' });
      await refresh();
    });
  };

  return (
    <Card>
      <CardHeader title={t('products.variantsTitle')} />
      <CardContent className="grid gap-3">
        <ul className="divide-y rounded-md border text-sm">
          {product.variants.map((v) => (
            <li key={v.id} className="grid gap-2 px-3 py-2">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <p className="font-mono">{v.sku}</p>
                  <p className="text-xs text-muted-foreground">
                    {[v.gtin, v.mpn].filter(Boolean).join(' · ') || '—'}
                  </p>
                </div>
                <label className="flex items-center gap-1.5 text-xs">
                  <Checkbox
                    disabled={!canWrite || busy}
                    checked={v.status === 'active'}
                    onChange={(e) =>
                      void run(async () => {
                        await api(`/catalog/variants/${v.id}`, {
                          method: 'PATCH',
                          body: { status: e.target.checked ? 'active' : 'inactive' },
                        });
                        await refresh();
                      })
                    }
                  />
                  {t('products.variantActive')}
                </label>
              </div>
              <PackageForm variant={v} canWrite={canWrite} onSaved={refresh} />
            </li>
          ))}
        </ul>
        {canWrite ? (
          <form onSubmit={add} className="grid gap-2">
            <div className="grid grid-cols-3 gap-2">
              {(['sku', 'gtin', 'mpn'] as const).map((field) => (
                <Input
                  key={field}
                  aria-label={t(`products.${field}`)}
                  placeholder={t(`products.${field}`)}
                  required={field === 'sku'}
                  value={draft[field]}
                  onChange={(e) => {
                    setDraft({ ...draft, [field]: e.target.value });
                  }}
                />
              ))}
            </div>
            <Button type="submit" variant="outline" size="sm" disabled={busy}>
              {t('products.addVariant')}
            </Button>
          </form>
        ) : null}
        {error ? <Alert variant="destructive">{error}</Alert> : null}
      </CardContent>
    </Card>
  );
}

export function MediaSection({ product }: { product: Product }) {
  const { t } = useTranslation();
  const canWrite = useCan('catalog.write');
  const refresh = useRefresh(product.id);
  const { busy, error, run } = useAction();
  const [role, setRole] = useState<ProductMedia['role']>(
    product.media.some((m) => m.role === 'main') ? 'gallery' : 'main',
  );

  const upload = (file: File) =>
    void run(async () => {
      const form = new FormData();
      // Поля — до файла: сервер читает поля, пришедшие раньше файла.
      form.append('role', role);
      form.append('file', file);
      await api(`/catalog/products/${product.id}/images`, { method: 'POST', form });
      await refresh();
    });

  return (
    <Card>
      <CardHeader title={t('products.mediaTitle')} description={t('products.imageHint')} />
      <CardContent className="grid gap-3">
        <ul className="grid grid-cols-2 gap-2">
          {product.media.map((m) => (
            <li key={m.id} className="relative rounded-md border p-2 text-xs">
              {MEDIA_URL ? (
                <img
                  src={`${MEDIA_URL}/${m.asset.s3Key}`}
                  alt=""
                  loading="lazy"
                  className="mb-1 aspect-square w-full rounded object-contain"
                />
              ) : (
                <p className="mb-1 truncate font-mono" title={m.asset.s3Key}>
                  {m.asset.s3Key.split('/').pop()}
                </p>
              )}
              <div className="flex items-center justify-between">
                <Badge variant={m.role === 'main' ? 'default' : 'secondary'}>
                  {t(`products.role.${m.role}`)}
                </Badge>
                <span className="text-muted-foreground">
                  {m.asset.width && m.asset.height
                    ? `${String(m.asset.width)}×${String(m.asset.height)}`
                    : ''}
                </span>
              </div>
              {canWrite ? (
                <button
                  type="button"
                  aria-label={t('common.remove')}
                  className="absolute right-1 top-1 rounded bg-background/80 p-1 hover:text-destructive"
                  disabled={busy}
                  onClick={() =>
                    void run(async () => {
                      await api(`/catalog/product-images/${m.id}`, { method: 'DELETE' });
                      await refresh();
                    })
                  }
                >
                  <Trash2 className="size-3.5" />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
        {canWrite ? (
          <div className="grid grid-cols-[8rem_1fr] gap-2">
            <Select
              aria-label={t('common.status')}
              value={role}
              onChange={(e) => {
                setRole(e.target.value as ProductMedia['role']);
              }}
            >
              {(['main', 'gallery', 'swatch'] as const).map((r) => (
                <option key={r} value={r}>
                  {t(`products.role.${r}`)}
                </option>
              ))}
            </Select>
            <Input
              type="file"
              aria-label={t('products.uploadImage')}
              accept="image/jpeg,image/png,image/webp,image/avif"
              disabled={busy}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) upload(file);
                e.target.value = '';
              }}
            />
          </div>
        ) : null}
        {error ? <Alert variant="destructive">{error}</Alert> : null}
      </CardContent>
    </Card>
  );
}

/** Число из поля ввода («5,4» → 5.4); пусто — null. */
const parseInput = (s: string): number | null => {
  const n = Number(s.trim().replace(',', '.'));
  return s.trim() === '' || !Number.isFinite(n) || n <= 0 ? null : n;
};
const fromMinor = (n: number | null, unit: number) => (n ? String(n / unit) : '');

/** Упаковка варианта: вес (кг) и коробка (см) — по ним считается доставка. */
function PackageForm({
  variant,
  canWrite,
  onSaved,
}: {
  variant: Variant;
  canWrite: boolean;
  onSaved: () => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const { busy, error, run } = useAction();
  const initial = {
    weight: fromMinor(variant.weightG, 1000),
    length: fromMinor(variant.lengthMm, 10),
    width: fromMinor(variant.widthMm, 10),
    height: fromMinor(variant.heightMm, 10),
  };
  const [form, setForm] = useState(initial);
  const dirty = (Object.keys(form) as (keyof typeof form)[]).some((k) => form[k] !== initial[k]);

  const save = (e: SyntheticEvent) => {
    e.preventDefault();
    const toInt = (s: string, unit: number) => {
      const n = parseInput(s);
      return n === null ? undefined : Math.round(n * unit);
    };
    const body = {
      weightG: toInt(form.weight, 1000),
      lengthMm: toInt(form.length, 10),
      widthMm: toInt(form.width, 10),
      heightMm: toInt(form.height, 10),
    };
    void run(async () => {
      await api(`/catalog/variants/${variant.id}`, { method: 'PATCH', body });
      await onSaved();
    });
  };

  return (
    <form onSubmit={save} className="grid gap-1">
      <p className="text-xs text-muted-foreground">{t('products.package')}</p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        {(['weight', 'length', 'width', 'height'] as const).map((field) => (
          <Input
            key={field}
            aria-label={t(`products.pkg.${field}`)}
            placeholder={t(`products.pkg.${field}`)}
            title={t(`products.pkg.${field}`)}
            inputMode="decimal"
            disabled={!canWrite || busy}
            value={form[field]}
            onChange={(e) => {
              setForm({ ...form, [field]: e.target.value });
            }}
          />
        ))}
        {canWrite ? (
          <Button type="submit" variant="outline" size="sm" disabled={busy || !dirty}>
            {t('common.save')}
          </Button>
        ) : null}
      </div>
      {error ? <Alert variant="destructive">{error}</Alert> : null}
    </form>
  );
}
